import type { DefaultMantineColor, VariantColorsResolver } from '@mantine/core';
import {
  Anchor,
  CheckboxGroup,
  createTheme,
  defaultVariantColorsResolver,
  InputWrapper,
  Modal,
  MultiSelect,
  Notification,
  NumberInput,
  PasswordInput,
  Pill,
  parseThemeColor,
  PinInput,
  Radio,
  rem,
  rgba,
  SegmentedControl,
  Select,
  Switch,
  TextInput,
} from '@mantine/core';
import { DateInput, DateTimePicker } from '@mantine/dates';

// Define your custom color names
type ExtendedCustomColors =
  | 'gigi-black'
  | 'gigi-grey-text'
  | 'gigi-grey'
  | 'gigi-light-grey'
  | 'gigi-white'
  | 'gigi-aqua'
  | 'gigi-purple'
  | 'gigi-apple-pink'
  | 'gigi-orange'
  | 'gigi-outlines-dark'
  | 'gigi-outlines'
  | DefaultMantineColor; // Include default Mantine colors

type ColorValue = [string, string, string, string, string, string, string, string, string, string];

/**
 * One colour for all ten shades. Mantine 9 reads shade 9 for every theme colour when it builds
 * its CSS variables, so a missing shade crashes the whole app on start.
 */
const solid = (color: string) => Array.from({ length: 10 }, () => color) as ColorValue;

// Extend Mantine's color interface
declare module '@mantine/core' {
  export interface MantineThemeColorsOverride {
    colors: Record<ExtendedCustomColors, ColorValue>;
  }
}

const inputStyle = {
  label: {
    color: '#191919',
    fontWeight: 400,
    fontSize: '18px',
    lineHeight: '24px',
    marginBottom: '8px',
  },
  input: {
    color: '#191919',
    fontSize: '20px',
    fontWeight: 300,
    lineHeight: '32px',
    padding: '1rem 0.75rem',
    height: '64px',
    borderRadius: '8px',
  },
};

/**
 * Mantine 9 made the light variant solid. For a colour with a shade (`color="grape.5"`) it now
 * fills the background with that shade; Mantine 8 tinted it at 10% and used the shade for the
 * text, which is what the design uses (the dashboard tab counters). This restores the 8 behaviour
 * for that case; `cssVariablesResolver` below does the same for colours without a shade.
 */
const variantColorResolver: VariantColorsResolver = (input) => {
  const defaults = defaultVariantColorsResolver(input);
  if (input.variant !== 'light') return defaults;

  const parsed = parseThemeColor({ color: input.color ?? input.theme.primaryColor, theme: input.theme });
  if (!parsed.isThemeColor || parsed.shade === undefined) return defaults;

  const shade = input.theme.colors[parsed.color][parsed.shade];
  return {
    ...defaults,
    background: rgba(shade, 0.1),
    hover: rgba(shade, 0.12),
    color: `var(--mantine-color-${parsed.color}-${Math.min(parsed.shade, 6)})`,
  };
};

/** For MantineProvider: the translucent Mantine 8 light variant colours, see above. */
export { v8CssVariablesResolver as cssVariablesResolver } from '@mantine/core';

export const theme = createTheme({
  autoContrast: false,
  variantColorResolver,
  fontFamily: 'Outfit Variable, sans-serif',
  fontFamilyMonospace: 'Monaco, Courier, monospace',
  colors: {
    'gigi-black': solid('#191919'),
    'gigi-grey-text': solid('#808080'),
    'gigi-grey': solid('#C0C0C0'),
    'gigi-light-grey': solid('#F4F4F4'),
    'gigi-white': solid('#FFFFFF'),
    'gigi-aqua': solid('#32CCDC'),
    'gigi-purple': solid('#DC62DE'),
    'gigi-apple-pink': solid('#E861A4'),
    'gigi-orange': solid('#EE9F49'),
    'gigi-outlines-dark': solid('#3E3E3E'),
    'gigi-outlines': solid('#E8E8E8'),
  },
  headings: {
    fontFamily: 'Syne Variable, sans-serif',
    sizes: {
      h1: { fontSize: '3rem', lineHeight: '1.2', fontWeight: '700' },
      h2: { fontSize: '2.5rem', lineHeight: '1.2', fontWeight: '700' },
      h3: { fontSize: '2rem', lineHeight: '1.2', fontWeight: '700' },
      h4: { fontSize: '1.5rem', lineHeight: '1.2', fontWeight: '700' },
      h5: { fontSize: '1.25rem', lineHeight: '1.2', fontWeight: '700' },
      h6: { fontSize: '1rem', lineHeight: '1.2', fontWeight: '700' },
    },
  },
  spacing: {
    xxxs: rem(8),
    xxs: rem(16),
    xs: rem(24),
    sm: rem(32),
    md: rem(40),
    lg: rem(72),
    xl: rem(96),
    xxl: rem(120),
    xxxl: rem(160),
  },
  fontSizes: {
    xxs: rem(15),
    xs: rem(18),
    sm: rem(20),
    md: rem(24),
    lg: rem(32),
    xl: rem(48),
    xxl: rem(64),
  },
  radius: {
    xs: rem(8),
    sm: rem(16),
    md: rem(24),
  },
  defaultRadius: 'md',
  components: {
    TextInput: TextInput.extend({
      styles: { ...inputStyle },
    }),
    PasswordInput: PasswordInput.extend({
      styles: { ...inputStyle },
    }),
    InputWrapper: InputWrapper.extend({
      styles: { ...inputStyle },
    }),
    NumberInput: NumberInput.extend({
      styles: { ...inputStyle },
    }),
    Textarea: TextInput.extend({ styles: { ...inputStyle } }),
    Select: Select.extend({
      styles: {
        ...inputStyle,

        dropdown: {
          borderRadius: 8,
        },
        option: {
          fontWeight: 300,

          '&:hover': { backgroundColor: 'red' },
        },
      },
    }),
    CheckboxGroup: CheckboxGroup.extend({ styles: { ...inputStyle } }),
    DateTimePicker: DateTimePicker.extend({
      styles: { input: { ...inputStyle, padding: '1rem' } },
    }),
    DateInput: DateInput.extend({
      styles: { input: { ...inputStyle, padding: '1rem' } },
    }),
    MultiSelect: MultiSelect.extend({
      styles: {
        ...inputStyle,
        input: { minHeight: '3rem', padding: '0.5rem' },
        pill: {
          height: '2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
    }),
    Anchor: Anchor.extend({
      defaultProps: {
        underline: 'always',
        c: 'black',
      },
    }),
    PinInput: PinInput.extend({
      styles: {
        root: {
          gap: 16,
        },
        pinInput: { width: 64, height: 64 },
        input: {
          width: 64,
          height: 64,
          borderRadius: 8,
          color: '#191919',
          fontSize: 32,
          fontWeight: 400,
        },
      },
    }),
    Modal: Modal.extend({
      styles: {
        header: {
          padding: '24px 32px',
          paddingBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        },
        body: {
          padding: '0 32px 32px',
        },
        close: {
          position: 'absolute',
          top: 20,
          right: 20,
          width: 40,
          height: 40,
        },
      },
    }),
    SegmentedControl: SegmentedControl.extend({
      styles: { innerLabel: { fontSize: 18, fontWeight: 400 } },
    }),
    // Mantine 8 shows a check inside the thumb by default; keep the look the app was designed with.
    Switch: Switch.extend({ defaultProps: { withThumbIndicator: false }, styles: { label: { paddingInline: 8 } } }),
    Radio: Radio.extend({ styles: { label: { paddingInline: 8 } } }),
    Pill: Pill.extend({
      defaultProps: {
        bg: 'black',
        c: 'white',
      },
      styles: {
        root: {
          height: '2rem',
          padding: '0.5rem 0.75rem',
          borderRadius: '999px',
        },
        label: {
          fontSize: '15px',
          fontWeight: '500',
          lineHeight: '18px',
        },
      },
    }),
    Notification: Notification.extend({
      styles: {
        root: { padding: 8, borderRadius: 8 },
        body: { paddingLeft: 16 },
      },
    }),
  },
});
